import { useState } from 'react';
import useNotification from './useNotification';
import conversationService from '../services/conversationService';
import { renderMermaidDiagrams } from '../utils/mermaidRenderer';

/**
 * Custom hook for handling process flow diagram actions
 * Manages edit, save, and improve operations for Mermaid diagrams
 */
const useDiagramActions = (
  conversation,
  parsedDiagrams,
  setParsedDiagrams,
  diagramContentRef,
  currentVersion,
  updateCurrentVersion,
  isLocalUpdateRef,
  onConversationUpdate = null,
  showNotificationFn = null
) => {
  const { showNotification } = useNotification();
  const [editingDiagram, setEditingDiagram] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  /**
   * Reconstruct full markdown content from diagrams array
   * @param {Array} diagrams - Array of diagram objects
   * @returns {string} - Reconstructed markdown content
   */
  const reconstructContent = (diagrams) => {
    let content = '';
    diagrams.forEach(diagram => {
      content += `## ${diagram.title}\n\n`;
      if (diagram.descriptionBefore) content += `${diagram.descriptionBefore}\n\n`;
      content += `\`\`\`mermaid\n${diagram.code}\n\`\`\`\n\n`;
      if (diagram.descriptionAfter) content += `${diagram.descriptionAfter}\n\n`;
      if (diagram.keySteps && diagram.keySteps.length > 0) {
        content += `**Key Steps:**\n`;
        diagram.keySteps.forEach(step => {
          content += `- ${step}\n`;
        });
        content += '\n';
      }
    });
    return content.trim();
  };

  /**
   * Save updated diagrams to backend
   * @param {string} updatedContent - The full markdown content
   * @returns {Promise<Array>} - Returns the updated messages array for LHS sync
   */
  const saveToBackend = async (updatedContent) => {
    const updatedMessages = [...conversation.messages];
    let messageUpdated = false;

    // First try: Use messageIndex from currentVersion (most reliable)
    if (currentVersion && currentVersion.messageIndex !== undefined) {
      const idx = currentVersion.messageIndex;
      if (updatedMessages[idx] && updatedMessages[idx].role === 'assistant') {
        updatedMessages[idx] = {
          ...updatedMessages[idx],
          content: updatedContent
        };
        messageUpdated = true;
        console.log('✅ useDiagramActions: Updated message at messageIndex:', idx);
      }
    }

    // Fallback: Find and update the last assistant message
    if (!messageUpdated) {
      for (let i = updatedMessages.length - 1; i >= 0; i--) {
        if (updatedMessages[i].role === 'assistant') {
          updatedMessages[i] = {
            ...updatedMessages[i],
            content: updatedContent
          };
          console.log('⚠️ useDiagramActions: Used fallback - updated last assistant message at index:', i);
          break;
        }
      }
    }

    const conversationData = {
      messages: updatedMessages.map(msg => ({
        role: msg.role,
        content: msg.content,
        attachments: msg.attachments || [],
        createdAt: msg.createdAt || new Date().toISOString()
      }))
    };

    await conversationService.saveConversation(conversation._id, conversationData);

    return updatedMessages;
  };

  /**
   * Handler for opening the edit diagram dialog
   * @param {Object} diagram - The diagram to edit
   */
  const handleEditDiagram = (diagram) => {
    setEditingDiagram(diagram);
    setIsEditDialogOpen(true);
  };

  /**
   * Handler for closing the edit diagram dialog
   */
  const handleCloseEditDialog = () => {
    setIsEditDialogOpen(false);
    setEditingDiagram(null);
  };

  /**
   * Handler for saving edited diagram
   * @param {string} newCode - The updated Mermaid code
   * @param {string} newTitle - The updated diagram title (optional)
   * @returns {Promise<void>}
   */
  const handleSaveDiagramEdit = async (newCode, newTitle) => {
    if (!editingDiagram) return;

    try {
      // Update the diagram in parsedDiagrams (including title if provided)
      const updatedDiagrams = parsedDiagrams.map(diagram =>
        diagram.id === editingDiagram.id
          ? {
              ...diagram,
              code: newCode,
              // Update title if provided, otherwise keep the existing one
              title: newTitle !== undefined ? newTitle : diagram.title
            }
          : diagram
      );

      // Set the flag to prevent reset from currentVersion
      isLocalUpdateRef.current = true;
      setParsedDiagrams(updatedDiagrams);

      // Reconstruct the full content
      const updatedContent = reconstructContent(updatedDiagrams);
      diagramContentRef.current = updatedContent;

      // Update the current version
      if (updateCurrentVersion && currentVersion) {
        updateCurrentVersion({
          ...currentVersion,
          diagrams: updatedDiagrams,
          content: updatedContent
        });
      }

      // Save to backend and get updated messages
      const updatedMessages = await saveToBackend(updatedContent);

      // Sync to LHS immediately
      console.log('🔍 useDiagramActions: onConversationUpdate exists:', !!onConversationUpdate);
      console.log('🔍 useDiagramActions: updatedMessages count:', updatedMessages?.length);
      if (onConversationUpdate) {
        await onConversationUpdate(conversation._id, updatedMessages);
        console.log('✅ useDiagramActions: Synced edit to LHS');
      } else {
        console.log('⚠️ useDiagramActions: onConversationUpdate is null/undefined - LHS sync skipped!');
      }

      // Close edit dialog
      handleCloseEditDialog();

      const notifyFn = showNotificationFn || showNotification;
      notifyFn({
        message: "Diagram updated successfully",
        type: "success"
      });

      // Re-render diagrams
      setTimeout(() => {
        renderMermaidDiagrams(1);
      }, 100);
    } catch (error) {
      console.error("Error saving diagram:", error);
      const notifyFn = showNotificationFn || showNotification;
      notifyFn({
        message: "Failed to save diagram changes",
        type: "error"
      });
    }
  };

  /**
   * Handler for updating diagram from improvement modal
   * @param {Object} improveDiagram - The diagram being improved
   * @param {string} improvedContent - The improved Mermaid code
   * @returns {Promise<void>}
   */
  const handleUpdateImprovedContent = async (improveDiagram, improvedContent) => {
    if (!improveDiagram || !improvedContent) return;

    try {
      // Handle remove action
      if (improveDiagram.action === 'remove') {
        const updatedDiagrams = parsedDiagrams.filter(d => d.id !== improveDiagram.id);
        setParsedDiagrams(updatedDiagrams);
        return true;
      }

      // Update the diagram with improved content
      const updatedDiagrams = parsedDiagrams.map(d => {
        if (d.id === improveDiagram.id) {
          return {
            ...d,
            code: improvedContent
          };
        }
        return d;
      });

      // Set the flag to prevent reset from currentVersion
      isLocalUpdateRef.current = true;
      setParsedDiagrams(updatedDiagrams);

      // Reconstruct the full content
      const updatedContent = reconstructContent(updatedDiagrams);
      diagramContentRef.current = updatedContent;

      // Update the current version
      if (updateCurrentVersion && currentVersion) {
        updateCurrentVersion({
          ...currentVersion,
          diagrams: updatedDiagrams,
          content: updatedContent
        });
      }

      // Save to backend and get updated messages
      const updatedMessages = await saveToBackend(updatedContent);

      // Sync to LHS immediately
      if (onConversationUpdate) {
        await onConversationUpdate(conversation._id, updatedMessages);
        console.log('✅ useDiagramActions: Synced improve to LHS');
      }

      const notifyFn = showNotificationFn || showNotification;
      notifyFn({
        message: 'Diagram improved successfully',
        type: 'success'
      });

      // Re-render diagrams
      setTimeout(() => {
        renderMermaidDiagrams(1);
      }, 100);

      return true;
    } catch (error) {
      console.error("Error updating improved content:", error);
      const notifyFn = showNotificationFn || showNotification;
      notifyFn({
        message: 'Failed to update improved content',
        type: 'error'
      });
      return false;
    }
  };

  return {
    editingDiagram,
    isEditDialogOpen,
    handleEditDiagram,
    handleCloseEditDialog,
    handleSaveDiagramEdit,
    handleUpdateImprovedContent
  };
};

export default useDiagramActions;
